import React from 'react';
import PropTypes from 'prop-types';
import { NavLink } from 'react-router-dom';
import { useDictionary } from '../../../../utilities/hooks/UseDictionary';
import { NavigatorPaths } from '../../../../assets/dictionaries/NavigatorPaths';
import ImageHelper from '../../../../utilities/helpers/ImageHelper';
import pencil from '../../../../assets/icons/ic_pencil.svg';
import trash from '../../../../assets/icons/ic_trash.svg';
import placeholder from '../../../../assets/images/bg_placeholder_square.svg';

const SECTION_LABEL_KEYS = {
	recommended_place: 'recommendation_section_short_place',
	recommended_food: 'recommendation_section_short_food'
};

// A single recommendation. Image display mirrors the experience list items:
//   list mode  → fixed 2x2 grid of 4 thumbs (asset[i] or placeholder)
//   grid mode  → fixed row of 3 thumbs (asset[i] or placeholder)
const RecommendationListItem = ({ recommendation, onDeleteClicked, compact }) => {
	const dictionary = useDictionary();
	const assets = recommendation?.assets ?? [];
	const sectionLabelKey = SECTION_LABEL_KEYS[recommendation?.relation_type];
	const editPath =
		'/' +
		NavigatorPaths.recommendations_edit.path.replace(':id', recommendation.id);

	// One thumb slot — shows the asset at index i, or the placeholder. Matches
	// the fixed-slot logic the experience list items use.
	const thumbSlot = (className, i) => (
		<img
			key={i}
			className={className}
			onError={(e) => {
				e.currentTarget.src = placeholder;
			}}
			src={
				assets[i]
					? ImageHelper.getFullImageUrl(
							assets[i].thumbnail_url || assets[i].file_url
					  )
					: placeholder
			}
			alt=""
		/>
	);

	if (compact) {
		return (
			<div className="experience-item-compact-container">
				<h3 style={{ marginTop: '12px' }}>{recommendation?.name}</h3>
				<div style={{ marginTop: '10px' }} className="text ellipsize">
					{recommendation?.description}
				</div>
				<div className="flex-filler" />
				<div className="experience-item-buttons-container-compact">
					<NavLink to={editPath} className="experience-item-button-container">
						<img src={pencil} alt="" />
						<h4 style={{ fontSize: '12px' }}>{dictionary.edit}</h4>
					</NavLink>
					<div
						onClick={() => onDeleteClicked(recommendation)}
						className="experience-item-button-container"
					>
						<img src={trash} alt="" />
						<h4 style={{ fontSize: '12px' }}>{dictionary.delete}</h4>
					</div>
				</div>
				<div className="experience-compact-thumbs-container">
					{[0, 1, 2].map((i) => thumbSlot('experience-compact-thumb', i))}
				</div>
			</div>
		);
	}

	return (
		<div className="experience-item-full-container">
			<div className="experience-item-basic-info-container">
				<div className="flex-filler" />
				<h3 style={{ marginTop: '12px' }}>{recommendation?.name}</h3>
				<div style={{ marginTop: '10px' }} className="text ellipsize">
					{recommendation?.description}
				</div>
				<div className="experience-item-buttons-container-full">
					<NavLink to={editPath} className="experience-item-button-container">
						<img src={pencil} alt="" />
						<h4 style={{ fontSize: '12px' }}>{dictionary.edit}</h4>
					</NavLink>
					<div
						onClick={() => onDeleteClicked(recommendation)}
						className="experience-item-button-container"
					>
						<img src={trash} alt="" />
						<h4 style={{ fontSize: '12px' }}>{dictionary.delete}</h4>
					</div>
				</div>
			</div>

			<div className="experience-thumbs-container">
				<div className="image-wrapper">
					{[0, 1, 2, 3].map((i) => thumbSlot('experience-thumb', i))}
				</div>
			</div>

			<div className="experience-item-divider" />
			<div className="experience-small-column">
				<h4 style={{ fontSize: '12px' }}>{dictionary.details}</h4>
				<div className="experience-info-rows-container">
					<div
						className="experience-item-row"
						style={{ marginBottom: '6px', maxWidth: '200px' }}
					>
						<div className="experience-info-title" style={{ opacity: '0.6' }}>
							{dictionary.recommendation_section}
						</div>
						<div className="experience-info-title">
							{sectionLabelKey ? dictionary[sectionLabelKey] : '-'}
						</div>
					</div>
					<div style={{ marginBottom: '6px', maxWidth: '200px' }}>
						<div className="experience-info-title" style={{ opacity: '0.6' }}>
							{dictionary.recommendation_location}
						</div>
						<div
							className="experience-info-title"
							style={{ maxWidth: 'unset', marginTop: '2px' }}
						>
							{recommendation?.point_of_interest?.name}
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

RecommendationListItem.propTypes = {
	recommendation: PropTypes.object.isRequired,
	onDeleteClicked: PropTypes.func.isRequired,
	compact: PropTypes.bool
};

RecommendationListItem.defaultProps = {
	compact: false
};

export default RecommendationListItem;
